import React, { useState, useMemo } from 'react';
import { 
  CheckCircle2, 
  Sparkles, 
  ArrowLeft, 
  ShieldCheck, 
  Gift, 
  Award, 
  Users, 
  TrendingUp, 
  HeartHandshake, 
  ExternalLink, 
  BookOpen, 
  GraduationCap, 
  Bot, 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  Check, 
  ShoppingBag, 
  Percent, 
  Truck, 
  Clock, 
  PhoneCall, 
  ArrowRight,
  Calculator,
  Zap,
  HelpCircle,
  Home,
  MessageCircle,
  CheckCircle
} from 'lucide-react';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';

interface GiffarineMemberPageProps {
  onNavigateHome: () => void;
  onOpenChatAndShop?: () => void;
}

const OFFICIAL_REGISTER_URL = 'https://www.giffarine.com/member/register/132000061';
const INTRODUCER_CODE = '132000061';
const INTRODUCER_NAME = 'PINKGIFF (รหัสผู้แนะนำอย่างเป็นทางการ)';

export const GiffarineMemberPage: React.FC<GiffarineMemberPageProps> = ({ 
  onNavigateHome,
  onOpenChatAndShop 
}) => {
  const [copiedCode, setCopiedCode] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [monthlySpend, setMonthlySpend] = useState<number>(2500);

  const handleCopyCode = () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(INTRODUCER_CODE).catch(() => {});
      }
    } catch (e) {
      console.warn('Clipboard error:', e);
    }
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2200);
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Calculations for dynamic bubble widget
  const calculations = useMemo(() => {
    const regularSpend = monthlySpend;
    // 25% discount means member pays 75%
    const memberCost = Math.round(regularSpend * 0.75);
    const instantSavingsMonth = regularSpend - memberCost;
    const instantSavingsYear = instantSavingsMonth * 12;
    // PV estimate is approx 70-75% of retail, or equals member price in many product categories
    const pvMonth = Math.round(memberCost * 0.8);
    // Estimated dividend (10% min to 15% tier)
    const estimatedDividendMonth = Math.round(pvMonth * 0.10);
    const totalBenefitYear = instantSavingsYear + (estimatedDividendMonth * 12);
    const breakEvenMonths = (180 / Math.max(1, instantSavingsMonth)).toFixed(1);

    return {
      regularSpend,
      memberCost,
      instantSavingsMonth,
      instantSavingsYear,
      pvMonth,
      estimatedDividendMonth,
      totalBenefitYear,
      breakEvenMonths
    };
  }, [monthlySpend]);

  const spendPresets = [1000, 2500, 5000, 10000];

  const benefits = [
    {
      id: 'discount',
      badge: 'ประหยัดทันที 25%',
      icon: Percent,
      title: 'ส่วนลด 25% ตลอดชีพ',
      desc: 'รับสิทธิ์ซื้อสินค้ากิฟฟารีนทุกหมวดหมู่ ไม่ว่าจะเป็นอาหารเสริม สกินแคร์ หรือของใช้ในบ้าน ลดทันที 25% ตั้งแต่ชิ้นแรกที่สั่งซื้อ',
      bubbleTone: 'from-pink-500/10 via-rose-500/5 to-white',
      accentColor: 'text-pink-600',
      badgeColor: 'bg-pink-100 text-pink-700 border-pink-200'
    },
    {
      id: 'lifetime',
      badge: '180.- จ่ายครั้งเดียว',
      icon: Gift,
      title: 'สมาชิกตลอดชีพ ไม่มีหมดอายุ',
      desc: 'จ่ายค่าธรรมเนียมเพียง 180 บาทครั้งเดียวจบ เป็นสมาชิกตลอดชีพ ไม่ต้องต่ออายุรายปี ไม่ต้องกังวลเรื่องสถานะสมาชิกถูกตัดสิทธิ์',
      bubbleTone: 'from-amber-500/10 via-orange-500/5 to-white',
      accentColor: 'text-amber-600',
      badgeColor: 'bg-amber-100 text-amber-800 border-amber-200'
    },
    {
      id: 'dividend',
      badge: 'คืนเงิน 10% - 25%',
      icon: TrendingUp,
      title: 'เงินปันผลคืนเข้าบัญชี (PV)',
      desc: 'เปลี่ยนรายจ่ายเป็นรายรับ ทุกยอดการสั่งซื้อคิดเป็นคะแนน PV สะสมเพื่อรับเงินปันผลคืน 10% - 25% โอนเข้าบัญชีธนาคารของคุณโดยตรงทุกเดือน',
      bubbleTone: 'from-emerald-500/10 via-teal-500/5 to-white',
      accentColor: 'text-emerald-600',
      badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200'
    },
    {
      id: 'insurance',
      badge: 'คุ้มครองสูงสุด 320,000.-',
      icon: ShieldCheck,
      title: 'ประกันอุบัติเหตุคุ้มครองฟรี',
      desc: 'รับความคุ้มครองประกันภัยอุบัติเหตุฟรี วงเงิน 120,000 ถึง 320,000 บาท คุ้มครองครอบคลุมทั้งสมาชิกและคู่สมรสตามเงื่อนไขบริษัท',
      bubbleTone: 'from-blue-500/10 via-sky-500/5 to-white',
      accentColor: 'text-blue-600',
      badgeColor: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      id: 'academy',
      badge: 'เรียนฟรี + AI Coach',
      icon: GraduationCap,
      title: 'ระบบเรียนรู้ & AI อัจฉริยะ',
      desc: 'สิทธิพิเศษเข้าเรียนหลักสูตรความรู้ผลิตภัณฑ์ การตลาดออนไลน์จากสถาบันกิฟฟารีน พร้อมผู้ช่วย AI พิงค์กิฟ ให้คำปรึกษาตลอด 24 ชั่วโมง',
      bubbleTone: 'from-purple-500/10 via-fuchsia-500/5 to-white',
      accentColor: 'text-purple-600',
      badgeColor: 'bg-purple-100 text-purple-800 border-purple-200'
    },
    {
      id: 'legacy',
      badge: 'ส่งต่อเป็นมรดกได้',
      icon: HeartHandshake,
      title: 'มรดกทางธุรกิจให้ลูกหลาน',
      desc: 'เครือข่ายผู้บริโภคและรายได้ที่คุณสร้างขึ้นเป็นทรัพย์สินที่มั่นคง สามารถทำเรื่องส่งต่อกรรมสิทธิ์เป็นมรดกให้ทายาทตามกฎหมายได้จริง',
      bubbleTone: 'from-rose-500/10 via-pink-500/5 to-white',
      accentColor: 'text-rose-600',
      badgeColor: 'bg-rose-100 text-rose-800 border-rose-200'
    }
  ];

  const faqs = [
    {
      q: 'สมัครสมาชิกกิฟฟารีนแล้ว มีข้อผูกมัดหรือต้องรักษายอดรายเดือนไหม?',
      a: 'ไม่มีข้อผูกมัดและไม่ต้องรักษายอดรายเดือนใดๆ ทั้งสิ้นค่ะ! สมัครครั้งเดียว 180 บาท เป็นสมาชิกตลอดชีพ ซื้อสินค้าชิ้นเดียวเมื่อไหร่ก็ได้ลด 25% ทันที สบายใจ 100% สไตล์เพื่อนแนะนำเพื่อนค่ะ'
    },
    {
      q: 'รหัสสมาชิกมีวันหมดอายุไหม ต้องต่ออายุรายปีหรือไม่?',
      a: 'ไม่มีวันหมดอายุค่ะ! สมาชิกกิฟฟารีนเป็นสมาชิกตลอดชีพ (Lifetime Membership) จ่ายค่าสมัครเพียงครั้งเดียว 180 บาท ไม่ต้องจ่ายต่ออายุรายปี และคงสิทธิ์ส่วนลด 25% และสะสมแต้ม PV ตลอดชีพ'
    },
    {
      q: 'ชุดคู่มือและเอกสารการสมัครจะได้รับอะไรบ้าง?',
      a: 'ได้รับคู่มือผลิตภัณฑ์กิฟฟารีนสี่สีทั้งเล่ม (Product Catalog), หนังสือแผนผลประโยชน์และคู่มือธุรกิจ, คู่มือดูแลสุขภาพ, บัตรสมาชิกพร้อมรหัสประจำตัวสำหรับใช้สั่งซื้อสินค้าทั้งหน้าร้านและออนไลน์ พร้อมสิทธิ์ประกันอุบัติเหตุฟรี'
    },
    {
      q: 'ถ้าต้องการสร้างเป็นรายได้เสริม มีทีมงานช่วยแนะนำไหม?',
      a: 'มีทีมงานพิงค์กิฟและระบบ AI Coach คอยให้คำปรึกษาตลอดเส้นทางค่ะ อีกทั้งกิฟฟารีนมีคอร์สอบรมออนไลน์ สื่อการสอน และกลุ่มเรียนรู้แบบจับมือทำฟรี ไม่จำเป็นต้องมีประสบการณ์มาก่อนก็สามารถเริ่มต้นได้ทันที'
    },
    {
      q: 'สั่งซื้อสินค้าผ่านช่องทางไหนได้บ้าง?',
      a: 'สามารถสั่งซื้อผ่านเว็บไซต์ GIFFFORYOU.COM, แอปพลิเคชันกิฟฟารีน, เดลิเวอรี่ส่งตรงถึงบ้าน หรือสำนักงานสาขากิฟฟารีนทั่วประเทศ มีบริการจัดส่งพัสดุด่วนถึงหน้าบ้านทุกวันค่ะ'
    }
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_#FFF5F8_0%,_#F8FAFC_50%,_#EFF6FF_100%)] text-slate-800 font-['Prompt',sans-serif] selection:bg-pink-500 selection:text-white pb-28">
      
      {/* Dynamic Specular Background Glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-pink-300/25 rounded-full blur-3xl" />
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-rose-200/25 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 left-1/3 w-80 h-80 bg-fuchsia-200/20 rounded-full blur-3xl" />
      </div>

      {/* Standalone Bubble Top Bar (No Header/Footer constraint strictly adhered) */}
      <header className="sticky top-0 z-30 px-3 sm:px-6 pt-3 sm:pt-4">
        <div className="max-w-6xl mx-auto">
          {/* Glass Bubble Pill Container */}
          <div className="relative rounded-full bg-white/80 backdrop-blur-xl border border-white/80 shadow-lg shadow-pink-500/5 px-3 sm:px-5 py-2.5 flex items-center justify-between gap-2 sm:gap-4 transition-all">
            
            {/* Top specular reflection */}
            <div className="absolute top-1 left-8 right-8 h-[1.5px] bg-gradient-to-r from-transparent via-white to-transparent opacity-90 pointer-events-none" />

            {/* Left: Back Button & Brand */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={onNavigateHome}
                className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100/90 hover:bg-blue-50 text-slate-700 hover:text-[#2A59FE] border border-slate-200/60 hover:border-blue-200 text-xs font-bold transition-all active:scale-95 cursor-pointer"
                title="กลับสู่หน้าร้านหลัก"
              >
                <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                <span className="hidden sm:inline">กลับสู่หน้าร้าน</span>
                <span className="sm:hidden">หน้าร้าน</span>
              </button>

              <div className="h-4 w-[1px] bg-slate-200 hidden sm:block" />

              <div className="flex items-center gap-2">
                <img 
                  src={GIFFARINE_OFFICIAL_LOGO} 
                  alt="Giffarine" 
                  className="h-6 sm:h-7 w-auto object-contain"
                />
                <span className="hidden md:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-50 border border-blue-200/60 text-[10.5px] font-black text-[#1E3A8A] uppercase tracking-wider">
                  <Sparkles className="w-3 h-3 text-[#2A59FE]" />
                  VIP Member Privilege
                </span>
              </div>
            </div>

            {/* Right: Quick Action Bubbles */}
            <div className="flex items-center gap-1.5 sm:gap-2.5">
              {onOpenChatAndShop && (
                <button
                  onClick={onOpenChatAndShop}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 text-slate-700 hover:text-[#2A59FE] text-xs font-bold transition-all active:scale-95 cursor-pointer"
                >
                  <Bot className="w-3.5 h-3.5 text-[#2A59FE]" />
                  <span>ปรึกษา AI พิงค์กิฟ</span>
                </button>
              )}

              <a
                href={OFFICIAL_REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white text-xs font-black shadow-md shadow-blue-500/25 hover:shadow-lg hover:shadow-blue-500/35 border border-white/40 transition-all hover:scale-102 active:scale-95 cursor-pointer whitespace-nowrap"
              >
                <span>สมัครสมาชิก 180.-</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 sm:pt-8 space-y-8 sm:space-y-12">

        {/* ========================================================
            HERO 3D METALLIC PEARL BUBBLE CONTAINER
        ======================================================== */}
        <section className="relative rounded-[32px] sm:rounded-[40px] bg-[radial-gradient(ellipse_at_30%_20%,_#FFFFFF_0%,_#FFF1F6_30%,_#FDE8F0_70%,_#F8E0EB_100%)] border-2 sm:border-[3px] border-white shadow-2xl shadow-pink-900/10 p-5 sm:p-10 md:p-12 overflow-hidden transition-all">
          
          {/* Specular 3D Bubble Flares */}
          <div className="absolute top-2 left-6 right-6 h-[2.5px] bg-gradient-to-r from-transparent via-white to-transparent opacity-95 pointer-events-none" />
          <div className="absolute top-3 left-10 w-24 h-1 rounded-full bg-white/80 blur-[0.5px] pointer-events-none" />
          <div className="absolute -bottom-16 -right-16 w-80 h-80 rounded-full bg-gradient-to-br from-pink-400/20 to-rose-500/10 blur-2xl pointer-events-none" />
          <div className="absolute top-1/2 -left-20 w-60 h-60 rounded-full bg-white/40 blur-xl pointer-events-none" />

          {/* Inner Content Grid */}
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            
            {/* Left Column (Text & Value Proposition) */}
            <div className="lg:col-span-7 space-y-4 sm:space-y-5 text-center lg:text-left">
              
              {/* Floating Highlight Pill Bubble */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-[#1E3A8A] border border-blue-200/80 shadow-xs text-xs font-black">
                <span className="w-2 h-2 rounded-full bg-[#2A59FE] animate-ping" />
                <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span>สิทธิประโยชน์สมาชิกระดับ VIP • คุ้มค่าตลอดชีพ</span>
              </div>

              {/* Display Typography */}
              <h1 className="text-2xl sm:text-4xl lg:text-[42px] font-black tracking-tight text-slate-900 leading-[1.25]">
                สมัครกิฟฟารีน (Giffarine VIP) <br className="hidden sm:inline" />
                <span className="bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] bg-clip-text text-transparent">
                  เปลี่ยนรายจ่าย เป็นส่วนลด 25% ตลอดชีพ
                </span>
              </h1>

              {/* Friendly Subtitle */}
              <p className="text-xs sm:text-sm md:text-base text-slate-600 font-medium leading-relaxed max-w-2xl mx-auto lg:mx-0">
                สมัครครั้งเดียวเพียง <strong className="text-slate-900 font-extrabold underline decoration-blue-400 decoration-2">180 บาท</strong> เป็นสมาชิกตลอดชีพ 
                ไม่ต้องรักษายอด ไม่มีหมดอายุรายปี ลดทันที 25% ตั้งแต่ชิ้นแรก พร้อมเงินปันผลคืน 10% - 25% และประกันอุบัติเหตุฟรี สไตล์เพื่อนแนะนำเพื่อน
              </p>

              {/* Floating Benefit Micro-Bubbles */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 pt-1">
                <span className="px-3 py-1.5 rounded-full bg-white/80 border border-blue-200/80 text-[11px] font-bold text-slate-700 shadow-2xs flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  ลด 25% ทันทีทุกชิ้น
                </span>
                <span className="px-3 py-1.5 rounded-full bg-white/80 border border-blue-200/80 text-[11px] font-bold text-slate-700 shadow-2xs flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  180.- จ่ายครั้งเดียวจบ
                </span>
                <span className="px-3 py-1.5 rounded-full bg-white/80 border border-blue-200/80 text-[11px] font-bold text-slate-700 shadow-2xs flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  ปันผลคืน 10%-25% PV
                </span>
                <span className="px-3 py-1.5 rounded-full bg-white/80 border border-blue-200/80 text-[11px] font-bold text-slate-700 shadow-2xs flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  ประกันอุบัติเหตุฟรี
                </span>
              </div>

              {/* Action Buttons in Hero */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
                <a
                  href={OFFICIAL_REGISTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-7 py-3.5 rounded-2xl bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white font-black text-sm sm:text-base shadow-xl shadow-blue-500/25 border-2 border-white/60 transition-all hover:scale-102 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>สมัครสมาชิกออนไลน์ (180 บ.)</span>
                  <ExternalLink className="w-4 h-4" />
                </a>

                {onOpenChatAndShop && (
                  <button
                    onClick={onOpenChatAndShop}
                    className="w-full sm:w-auto px-5 py-3.5 rounded-2xl bg-white hover:bg-blue-50/80 text-slate-700 hover:text-[#2A59FE] font-bold text-sm border-2 border-blue-200/80 shadow-xs transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Bot className="w-4 h-4 text-[#2A59FE]" />
                    <span>ปรึกษา AI ผู้ช่วยส่วนตัว</span>
                  </button>
                )}
              </div>

            </div>

            {/* Right Column: 3D Introducer Code Capsule Bubble */}
            <div className="lg:col-span-5">
              <div className="relative rounded-[28px] bg-gradient-to-b from-white via-white/95 to-pink-50/70 border-2 border-white shadow-xl shadow-pink-500/10 p-5 sm:p-6 text-center overflow-hidden">
                
                {/* Bubble specular highlight */}
                <div className="absolute top-1 left-4 right-4 h-[1.5px] bg-gradient-to-r from-transparent via-pink-200 to-transparent pointer-events-none" />
                
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-pink-500 to-rose-500 text-white mx-auto flex items-center justify-center shadow-md shadow-pink-500/25 mb-3">
                  <Award className="w-6 h-6 text-white" />
                </div>

                <div className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">
                  รหัสผู้แนะนำอย่างเป็นทางการ
                </div>
                <div className="text-xs font-semibold text-slate-700 mb-3">
                  (Official Introducer Code)
                </div>

                {/* Big Code Pill Bubble */}
                <div className="relative rounded-2xl bg-gradient-to-r from-pink-50 via-rose-50 to-pink-100/70 border-2 border-pink-300/70 p-3.5 mb-3 flex items-center justify-between gap-3 shadow-inner">
                  <div className="text-left pl-1">
                    <div className="text-[10px] font-bold text-[#E60067] uppercase tracking-wider">
                      รหัสผู้แนะนำสำหรับกรอกสมัคร
                    </div>
                    <div className="font-mono text-2xl sm:text-3xl font-black text-slate-900 tracking-wider">
                      {INTRODUCER_CODE}
                    </div>
                  </div>

                  <button
                    onClick={handleCopyCode}
                    className={`px-3.5 py-2.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all active:scale-90 cursor-pointer shadow-xs border ${
                      copiedCode 
                        ? 'bg-emerald-500 text-white border-emerald-400' 
                        : 'bg-white hover:bg-pink-50 text-[#E60067] border-pink-200'
                    }`}
                    title="คัดลอกรหัสผู้แนะนำ"
                  >
                    {copiedCode ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>คัดลอกแล้ว!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>คัดลอก</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Introducer details note */}
                <div className="text-[11px] text-slate-500 space-y-1 bg-white/70 rounded-xl p-2.5 border border-pink-100">
                  <div className="font-bold text-slate-800">ผู้แนะนำ: {INTRODUCER_NAME}</div>
                  <div>ระบบรับรองโดยตรงจาก บจก. กิฟฟารีน สกายไลน์ ยูนิตี้</div>
                </div>

                {/* Fast action */}
                <a
                  href={OFFICIAL_REGISTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 block w-full py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold shadow-md transition-all text-center cursor-pointer"
                >
                  คลิกลงทะเบียนพร้อมใส่รหัสผู้แนะนำอัตโนมัติ →
                </a>

              </div>
            </div>

          </div>
        </section>


        {/* ========================================================
            INTERACTIVE SAVINGS & PV DIVIDEND CALCULATOR BUBBLE
        ======================================================== */}
        <section className="relative rounded-[32px] bg-white border-2 border-white shadow-xl shadow-slate-200/50 p-5 sm:p-8 md:p-10 overflow-hidden">
          
          {/* Top light flare */}
          <div className="absolute top-2 left-8 right-8 h-[2px] bg-gradient-to-r from-transparent via-pink-200 to-transparent pointer-events-none" />

          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-50 border border-pink-200 text-[#E60067] text-xs font-black mb-2">
              <Calculator className="w-3.5 h-3.5" />
              <span>เครื่องคำนวณความคุ้มค่าแบบเรียลไทม์</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-slate-900">
              เป็นสมาชิกกิฟฟารีนแล้ว คุ้มค่าแค่ไหน?
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              ลองเลื่อนยอดใช้จ่ายของใช้ในบ้านและอาหารเสริมต่อเดือน เพื่อดูส่วนลดและเงินปันผลที่ได้รับคืน
            </p>
          </div>

          {/* Interactive Preset Pills */}
          <div className="max-w-xl mx-auto mb-6">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-slate-500 mr-1">เลือกยอดใช้จ่ายต่อเดือน:</span>
              {spendPresets.map((preset) => (
                <button
                  key={preset}
                  onClick={() => setMonthlySpend(preset)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
                    monthlySpend === preset
                      ? 'bg-[#E60067] text-white border-[#E60067] shadow-sm shadow-pink-500/20 scale-105'
                      : 'bg-slate-50 hover:bg-pink-50 text-slate-700 border-slate-200'
                  }`}
                >
                  ฿{preset.toLocaleString()}
                </button>
              ))}
            </div>

            {/* Range Slider */}
            <div className="mt-4 px-2">
              <input
                type="range"
                min="500"
                max="20000"
                step="500"
                value={monthlySpend}
                onChange={(e) => setMonthlySpend(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#E60067]"
              />
              <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-1">
                <span>฿500</span>
                <span className="font-bold text-[#E60067]">เลือก: ฿{monthlySpend.toLocaleString()} / เดือน</span>
                <span>฿20,000</span>
              </div>
            </div>
          </div>

          {/* Result Bubbles Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto">
            
            {/* Bubble 1: Instant 25% Discount */}
            <div className="rounded-2xl bg-gradient-to-br from-pink-50 via-white to-pink-50/30 border border-pink-200/80 p-4 text-center shadow-2xs">
              <div className="text-[11px] font-bold text-[#E60067] mb-1">
                ประหยัดทันที (ลด 25%)
              </div>
              <div className="text-xl sm:text-2xl font-black text-slate-900 font-mono">
                ฿{calculations.instantSavingsMonth.toLocaleString()}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                ต่อเดือน (ประหยัด ฿{calculations.instantSavingsYear.toLocaleString()} / ปี)
              </div>
            </div>

            {/* Bubble 2: PV Score */}
            <div className="rounded-2xl bg-gradient-to-br from-amber-50 via-white to-amber-50/30 border border-amber-200/80 p-4 text-center shadow-2xs">
              <div className="text-[11px] font-bold text-amber-700 mb-1">
                คะแนนสะสม PV
              </div>
              <div className="text-xl sm:text-2xl font-black text-slate-900 font-mono">
                {calculations.pvMonth.toLocaleString()}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                PV ต่อเดือน (สะสมขึ้นตำแหน่ง)
              </div>
            </div>

            {/* Bubble 3: Estimated Dividend */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 border border-emerald-200/80 p-4 text-center shadow-2xs">
              <div className="text-[11px] font-bold text-emerald-700 mb-1">
                เงินปันผลคืนเข้าบัญชี
              </div>
              <div className="text-xl sm:text-2xl font-black text-emerald-700 font-mono">
                ~฿{calculations.estimatedDividendMonth.toLocaleString()}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                ต่อเดือน (ตามแผนโบนัส 10-25%)
              </div>
            </div>

            {/* Bubble 4: Break-Even Highlight */}
            <div className="rounded-2xl bg-gradient-to-br from-purple-50 via-white to-purple-50/30 border border-purple-200/80 p-4 text-center shadow-2xs">
              <div className="text-[11px] font-bold text-purple-700 mb-1">
                คืนทุนค่าสมัคร 180.-
              </div>
              <div className="text-xl sm:text-2xl font-black text-purple-700 font-mono">
                บิลแรกทันที!
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                คุ้มค่ากำไรตลอดชีพนับแต่นี้
              </div>
            </div>

          </div>

          {/* Calculator Bottom Summary Pill */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-bold shadow-md">
              <Zap className="w-3.5 h-3.5 text-amber-300" />
              <span>รวมมูลค่าผลประโยชน์ที่คุณประหยัดและได้รับคืนกว่า <strong>฿{calculations.totalBenefitYear.toLocaleString()} บาท/ปี</strong></span>
            </div>
          </div>

        </section>


        {/* ========================================================
            6 CORE BENEFIT 3D GLASS BUBBLE CARDS
        ======================================================== */}
        <section className="space-y-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-50 border border-pink-200 text-[#E60067] text-xs font-black mb-2">
              <Gift className="w-3.5 h-3.5" />
              <span>เอกสิทธิ์สมาชิกกิฟฟารีน</span>
            </div>
            <h2 className="text-xl sm:text-3xl font-black text-slate-900">
              6 สิทธิประโยชน์ที่ได้รับทันทีเมื่อสมัครสมาชิก
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              ทำไมคนไทยกว่าหลายล้านครอบครัวจึงเลือกถือรหัสสมาชิกกิฟฟารีนติดตัวไว้ตลอดชีพ
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b) => {
              const IconComponent = b.icon;
              return (
                <div
                  key={b.id}
                  className={`relative rounded-[28px] bg-gradient-to-br ${b.bubbleTone} border-2 border-white shadow-lg shadow-pink-500/5 hover:shadow-xl hover:shadow-pink-500/10 p-6 transition-all duration-300 hover:-translate-y-1 group overflow-hidden`}
                >
                  {/* Top Specular Flare on each bubble */}
                  <div className="absolute top-1.5 left-6 right-6 h-[1.5px] bg-gradient-to-r from-transparent via-white to-transparent opacity-90 pointer-events-none" />

                  {/* Icon and Badge Header */}
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-white shadow-md shadow-slate-200/60 border border-white flex items-center justify-center group-hover:scale-110 transition-transform">
                      <IconComponent className={`w-6 h-6 ${b.accentColor}`} />
                    </div>
                    <span className={`text-[11px] font-extrabold px-3 py-1 rounded-full border shadow-2xs ${b.badgeColor}`}>
                      {b.badge}
                    </span>
                  </div>

                  {/* Card Content */}
                  <h3 className="text-base sm:text-lg font-black text-slate-900 mb-2">
                    {b.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 font-normal leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </section>


        {/* ========================================================
            3-STEP ENROLLMENT BUBBLE FLOW
        ======================================================== */}
        <section className="relative rounded-[32px] bg-gradient-to-br from-slate-900 via-slate-800 to-pink-950 border-2 border-slate-700/60 shadow-2xl p-6 sm:p-10 text-white overflow-hidden">
          
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="text-[11px] font-black uppercase tracking-wider text-pink-300 bg-white/10 px-3 py-1 rounded-full border border-white/10">
              EASY 3 STEPS
            </span>
            <h2 className="text-xl sm:text-3xl font-black text-white mt-2">
              ขั้นตอนการสมัครสมาชิกกิฟฟารีน ใน 3 สเต็ป
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 mt-1">
              สะดวกรวดเร็ว อนุมัติรหัสสมาชิกออนไลน์ทันทีผ่านระบบบริษัทแม่
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            
            {/* Connecting flow line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-16 right-16 h-[2px] bg-gradient-to-r from-pink-500/40 via-white/40 to-pink-500/40 -translate-y-8 pointer-events-none" />

            {/* Step 1 */}
            <div className="relative z-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-5 text-center flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#E60067] to-rose-500 text-white font-black text-lg flex items-center justify-center mb-3 shadow-lg shadow-pink-500/30 border-2 border-white/40">
                1
              </div>
              <h4 className="font-bold text-sm text-white mb-1">
                คลิกเข้าสู่ระบบสมัคร
              </h4>
              <p className="text-xs text-slate-300 leading-relaxed">
                เปิดหน้าลงทะเบียนกิฟฟารีนอย่างเป็นทางการ พร้อมระบุรหัสผู้แนะนำ <strong>{INTRODUCER_CODE}</strong>
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative z-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-5 text-center flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#E60067] to-rose-500 text-white font-black text-lg flex items-center justify-center mb-3 shadow-lg shadow-pink-500/30 border-2 border-white/40">
                2
              </div>
              <h4 className="font-bold text-sm text-white mb-1">
                กรอกข้อมูลและชำระ 180.-
              </h4>
              <p className="text-xs text-slate-300 leading-relaxed">
                กรอกข้อมูลส่วนบุคคล แนบสำเนาบัตรประชาชน และชำระค่าสมาชิกเพียง 180 บาท ผ่านช่องทางที่ปลอดภัย
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative z-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-5 text-center flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#E60067] to-rose-500 text-white font-black text-lg flex items-center justify-center mb-3 shadow-lg shadow-pink-500/30 border-2 border-white/40">
                3
              </div>
              <h4 className="font-bold text-sm text-white mb-1">
                รับรหัสสมาชิกทันที
              </h4>
              <p className="text-xs text-slate-300 leading-relaxed">
                รับรหัสสมาชิกกิฟฟารีนผ่าน SMS สามารถใช้สิทธิ์ซื้อสินค้าลด 25% และสะสมแต้ม PV ได้ทันที
              </p>
            </div>

          </div>

          <div className="mt-8 text-center">
            <a
              href={OFFICIAL_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#E60067] via-pink-600 to-rose-600 hover:from-[#C20048] hover:to-rose-700 text-white font-black text-sm sm:text-base rounded-full shadow-lg shadow-pink-500/30 border border-white/40 transition-all hover:scale-102 active:scale-95 cursor-pointer"
            >
              <span>เปิดหน้ารับสมัครกิฟฟารีนออนไลน์ (รหัสผู้แนะนำ {INTRODUCER_CODE})</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

        </section>


        {/* ========================================================
            FAQ ACCORDION BUBBLES
        ======================================================== */}
        <section className="relative rounded-[32px] bg-white border-2 border-white shadow-xl shadow-slate-200/50 p-6 sm:p-10">
          <div className="text-center max-w-xl mx-auto mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-50 border border-pink-200 text-[#E60067] text-xs font-black mb-2">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>Q&A สบายใจ ไร้ข้อผูกมัด</span>
            </div>
            <h2 className="text-xl sm:text-3xl font-black text-slate-900">
              คำถามที่พบบ่อยเกี่ยวกับการสมัครกิฟฟารีน
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              ไขทุกข้อสงสัยให้ชัดเจน สไตล์เพื่อนคุยเพื่อน สบายใจ 100%
            </p>
          </div>

          <div className="space-y-3 max-w-3xl mx-auto">
            {faqs.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div 
                  key={idx}
                  className={`rounded-2xl border transition-all overflow-hidden ${
                    isOpen 
                      ? 'bg-gradient-to-br from-pink-50/60 to-white border-pink-300 shadow-sm' 
                      : 'bg-slate-50/70 hover:bg-pink-50/30 border-slate-200/70'
                  }`}
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full px-5 py-4 text-left flex items-center justify-between gap-3 text-xs sm:text-sm font-bold text-slate-800 hover:text-[#E60067] transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E60067]" />
                      {faq.q}
                    </span>
                    {isOpen ? (
                      <ChevronUp className="w-4 h-4 text-[#E60067] shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-4 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-pink-100/80 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

      </main>

      {/* ========================================================
          STICKY BOTTOM FLOATING ACTION BUBBLE DOCK
      ======================================================== */}
      <div className="fixed bottom-4 left-0 right-0 z-40 px-4 pointer-events-none">
        <div className="max-w-xl mx-auto pointer-events-auto">
          <div className="relative rounded-full bg-slate-900/90 backdrop-blur-xl border border-white/20 shadow-2xl shadow-black/40 px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
            
            {/* Left action: Back to store */}
            <button
              onClick={onNavigateHome}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-slate-300 hover:text-white hover:bg-white/10 text-xs font-bold transition-all active:scale-95 cursor-pointer"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">หน้าร้านหลัก</span>
            </button>

            {/* Middle: AI Consultant */}
            {onOpenChatAndShop && (
              <button
                onClick={onOpenChatAndShop}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full text-pink-300 hover:text-pink-100 hover:bg-white/10 text-xs font-bold transition-all active:scale-95 cursor-pointer"
              >
                <Bot className="w-4 h-4 text-[#E60067]" />
                <span className="hidden sm:inline">ปรึกษา AI</span>
              </button>
            )}

            {/* Right: Big CTA Button */}
            <a
              href={OFFICIAL_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white text-xs sm:text-sm font-black shadow-lg shadow-blue-500/30 border border-white/40 transition-all hover:scale-102 active:scale-95 cursor-pointer whitespace-nowrap"
            >
              <span>สมัครกิฟฟารีน 180.-</span>
              <ArrowRight className="w-4 h-4" />
            </a>

          </div>
        </div>
      </div>

    </div>
  );
};
